import { Stack } from '@mui/material';
import { memo, useCallback, useMemo, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { MixedSwitch } from '@/modules/common/components/inputs';
import { getCommonBoolean } from '@/modules/common/helpers/commonValue';
import { WorkspaceMode } from '@modules/common/types/general';
import { LogicalSpace } from '@thive/fddm-types';
import { useAppStore } from '@/modules/app/store/useAppStore';

import { isDoNotStop, isRestrictedAccess, isSpeedLimit, isVehicleMode } from '../guards';
import { VehicleModeRule as VehicleModeRuleType } from '../types';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';

import { MaxSpeedRule } from './SpeedLimitRule';
import { VehicleModeRule } from './VehicleModeRule';
import { hasVehicleMode, useLogicalSpaceTrafficRuleActions } from '../hooks/useTrafficRulesActions';
import { UserPreferenceName } from '@/modules/userPreferences';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  logicalSpaces: LogicalSpace[];
}

/**
 * @description The vehicle mode rules that every logical space shares, keeping only the modes
 * all of them have. On a multi-selection, modes that only some spaces have show as off.
 */
const getSharedVehicleModeRules = (logicalSpaces: LogicalSpace[]): VehicleModeRuleType[] => {
  const [first, ...others] = logicalSpaces;
  if (!first) return [];

  return first.trafficRules.filter(isVehicleMode).flatMap((rule): VehicleModeRuleType[] => {
    const { vehicleTypeId, vehicleModes } = rule.vehicleModeExtension;
    const sharedModes = vehicleModes.filter((mode) =>
      others.every(({ trafficRules }) => hasVehicleMode(trafficRules, vehicleTypeId, mode)),
    );
    if (!isAtLeast1(sharedModes)) return [];

    return [
      {
        ...rule,
        vehicleModeExtension: { ...rule.vehicleModeExtension, vehicleModes: sharedModes },
      },
    ];
  });
};

/**
 * @description The vehicle types with a mode that only some logical spaces have, shown as 'Mixed'.
 */
const getMixedVehicleTypeIds = (logicalSpaces: LogicalSpace[]): ReadonlySet<string> =>
  new Set(
    logicalSpaces
      .flatMap(({ trafficRules }) => trafficRules.filter(isVehicleMode))
      .filter(({ vehicleModeExtension: { vehicleTypeId, vehicleModes } }) =>
        vehicleModes.some(
          (mode) =>
            !logicalSpaces.every(({ trafficRules }) =>
              hasVehicleMode(trafficRules, vehicleTypeId, mode),
            ),
        ),
      )
      .map(({ vehicleModeExtension: { vehicleTypeId } }) => vehicleTypeId),
  );

function TrafficRulePropertiesComponent({ logicalSpaces }: Props) {
  const mode = useAppStore((state) => state.mode);
  const { t } = useTranslation('interface');

  const { setDoNotStopEnabled, setRestrictedAccessEnabled, setMaxSpeed, toggleVehicleMode } =
    useLogicalSpaceTrafficRuleActions(logicalSpaces);

  // null when only some logical spaces have the rule, shown as 'Mixed' by MixedSwitch.
  // Switching a mixed rule on adds it to all of them
  const doNotStopEnabled = useMemo(
    () => getCommonBoolean(logicalSpaces.map(({ trafficRules }) => trafficRules.some(isDoNotStop))),
    [logicalSpaces],
  );

  const restrictedAccessEnabled = useMemo(
    () =>
      getCommonBoolean(
        logicalSpaces.map(({ trafficRules }) => trafficRules.some(isRestrictedAccess)),
      ),
    [logicalSpaces],
  );

  // Mixed when the logical spaces have different speed limits (or only some have one)
  const { speedLimitRule, isSpeedLimitMixed } = useMemo(() => {
    const speedLimitRules = logicalSpaces.map(({ trafficRules }) =>
      trafficRules.find(isSpeedLimit),
    );
    const maxSpeeds = new Set(speedLimitRules.map((rule) => rule?.speedLimitExtension.maxSpeed));
    const isMixed = maxSpeeds.size > 1;

    return { speedLimitRule: isMixed ? undefined : speedLimitRules[0], isSpeedLimitMixed: isMixed };
  }, [logicalSpaces]);

  const vehicleModeRules = useMemo(() => getSharedVehicleModeRules(logicalSpaces), [logicalSpaces]);
  const mixedVehicleTypeIds = useMemo(() => getMixedVehicleTypeIds(logicalSpaces), [logicalSpaces]);

  const handleDoNotStopChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDoNotStopEnabled(event.target.checked);
    },
    [setDoNotStopEnabled],
  );

  const handleRestrictedAccessChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRestrictedAccessEnabled(event.target.checked);
    },
    [setRestrictedAccessEnabled],
  );

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_LOGICAL_SPACE_TRAFFIC_RULES}
      title={t('traffic_rules.header', 'Traffic rules')}
      titleSx={{ fontWeight: 400 }}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <PropertiesLabel i18nkey='interface:traffic_rules.options.do_not_stop'>
          <Stack direction='row' justifyContent='flex-end'>
            <MixedSwitch
              checked={doNotStopEnabled}
              onChange={handleDoNotStopChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              inputProps={{
                'aria-label': t('traffic_rules.options.do_not_stop', 'Do not stop'),
              }}
            />
          </Stack>
        </PropertiesLabel>

        <PropertiesLabel
          i18nkey='interface:traffic_rules.options.restricted_access'
          labelRatio={70}
        >
          <Stack direction='row' justifyContent='flex-end'>
            <MixedSwitch
              checked={restrictedAccessEnabled}
              onChange={handleRestrictedAccessChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              inputProps={{
                'aria-label': t('traffic_rules.options.restricted_access', 'Restricted access'),
              }}
            />
          </Stack>
        </PropertiesLabel>

        <MaxSpeedRule
          rule={speedLimitRule}
          isMixed={isSpeedLimitMixed}
          onChange={setMaxSpeed}
          disabled={mode !== WorkspaceMode.EDITABLE}
        />

        <VehicleModeRule
          rules={vehicleModeRules}
          mixedVehicleTypeIds={mixedVehicleTypeIds}
          onToggle={toggleVehicleMode}
          disabled={mode !== WorkspaceMode.EDITABLE}
        />
      </Stack>
    </Accordion>
  );
}

export const TrafficRuleProperties = memo(TrafficRulePropertiesComponent);
export default TrafficRuleProperties;
